import {FC, useState, useEffect, useCallback} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {i18n} from '../../../../translate/i18n'
import {Loading} from '../../../../components/Loading/Loading'
import {useNavigate} from 'react-router-dom'
import useModals from '../../../../hooks/useModals'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'
import {Button} from '../../../../components/Button/Button'
import {Input} from '../../../../components/Input/Input'
import {POST, GET, DELETE, PUT} from '../../../../services/api'
import Swal from 'sweetalert2'
import SVG from 'react-inlinesvg'
import {CategoryType, IconType, SectionData, sectionType, Store} from './interfaces'
import CardStoreHotel from '../../../../components/CardStoreHotel/CardStoreHotel'
import {SVGUploadModal} from './components/SVGUploadModal'
import {IconEditModal} from './components/IconEditModal'
import useAuth from '../../../../hooks/useAuth'

const Content: FC = () => {
  const navigate = useNavigate()
  const {verifyAdm, verifyCommonAdmin} = useAuth()

  // Função para verificar se o usuário é admin
  const isUserAdmin = () => {
    return verifyCommonAdmin() && verifyAdm()
  }

  const [stores, setStores] = useState<Store[]>([])
  const [selectedStores, setSelectedStores] = useState<Store[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // Form states
  const [title, setTitle] = useState<string>('')
  const [type, setType] = useState<{value: sectionType; select: string}>({
    value: sectionType.category,
    select: String(i18n.t('hospitality_section.types.category')),
  })
  const [status, setStatus] = useState<boolean>(true)
  const [selectedItemId, setSelectedItemId] = useState<{
    selected: string
    value: number
  }>({
    selected: '',
    value: 0,
  })
  const [selectedIconId, setSelectedIconId] = useState<number>(0)
  const [selectedIconSvg, setSelectedIconSvg] = useState<string>('')

  // Data states
  const [categories, setCategories] = useState<CategoryType[]>([])
  const [icons, setIcons] = useState<IconType[]>([])
  const [iconsFiltered, setIconsFiltered] = useState<IconType[]>([])
  const [iconCategories, setIconCategories] = useState<any[]>([])
  const [selectedIconCategory, setSelectedIconCategory] = useState<any>(-1)
  const [iconSearchQuery, setIconSearchQuery] = useState<string>('')
  const [loadingIcons, setLoadingIcons] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingIcon, setEditingIcon] = useState<IconType | null>(null)

  // Estados para seleção múltipla
  const [selectedIcons, setSelectedIcons] = useState<number[]>([])
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false)

  // Filter states
  const [filterSection, setFilterSection] = useState<any>({
    label: 'Todos',
    value: 0,
  })

  // Form options
  const typeOptions = [
    {value: sectionType.category, select: String(i18n.t('hospitality_section.types.category'))},
    {value: sectionType.page, select: String(i18n.t('hospitality_section.types.page'))},
    // { value: sectionType.content, select: String(i18n.t('hospitality_section.types.content')) },
    //    { value: sectionType.simple, select: String(i18n.t('hospitality_section.types.simple')) },
    //   { value: sectionType.form, select: String(i18n.t('hospitality_section.types.form')) },
  ]

  const categoryOptions = categories.map((cat) => ({
    value: cat.id,
    select: cat.name,
  }))

  const changeFilterIcons = useCallback(() => {
    setLoadingIcons(true)

    const filtered = icons.filter((icon) => {
      let categoryMatch = true
      let searchMatch = true

      // Filtro por categoria
      if (parseInt(selectedIconCategory) !== -1) {
        categoryMatch = icon.category === selectedIconCategory
      }

      // Filtro por texto de busca
      if (iconSearchQuery.trim()) {
        const searchLower = iconSearchQuery.toLowerCase()
        searchMatch = icon.description?.toLowerCase().includes(searchLower)
      }

      return categoryMatch && searchMatch
    })

    setIconsFiltered(filtered)

    setTimeout(() => {
      setLoadingIcons(false)
    }, 300)
  }, [icons, selectedIconCategory, iconSearchQuery])

  const handleIconUploaded = async (newIcon: IconType) => {
    try {
      setShowUploadModal(false)
      setLoadingIcons(true)
      await fetchIcons()
      setLoadingIcons(false)

      setSelectedIconId(newIcon.id)
      setSelectedIconSvg(newIcon.svg)

      Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        text: 'Ícone carregado e lista atualizada automaticamente.',
        timer: 3000,
        showConfirmButton: false,
      })
    } catch (error) {
      console.error('Erro ao atualizar lista de ícones:', error)
      setLoadingIcons(false)

      const updatedIcons = [...icons, newIcon]
      setIcons(updatedIcons)
      setIconsFiltered(updatedIcons)

      setSelectedIconId(newIcon.id)
      setSelectedIconSvg(newIcon.svg)

      Swal.fire({
        icon: 'warning',
        title: 'Ícone adicionado!',
        text: 'O ícone foi adicionado, mas houve um problema ao atualizar a lista completa.',
        timer: 4000,
        showConfirmButton: false,
      })
    }
  }

  const handleIconUpdated = async (updatedIcon: any) => {
    try {
      await PUT(`/svgwebapp/${updatedIcon.id}`, {
        description: updatedIcon.name,
        svg: updatedIcon.svg_content,
      })

      const updatedIcons = icons.map((icon) =>
        icon.id === updatedIcon.id
          ? {...icon, description: updatedIcon.name, svg: updatedIcon.svg_content}
          : icon
      )
      setIcons(updatedIcons)
      changeFilterIcons()

      Swal.fire({
        title: 'Sucesso!',
        text: 'Ícone atualizado com sucesso.',
        icon: 'success',
        confirmButtonText: 'OK',
      })
    } catch (error) {
      console.error('Erro ao atualizar ícone:', error)
      Swal.fire({
        title: 'Erro!',
        text: 'Erro ao atualizar ícone.',
        icon: 'error',
        confirmButtonText: 'OK',
      })
      throw error
    }
  }

  const openEditModal = (icon: IconType) => {
    setEditingIcon(icon)
    setShowEditModal(true)
  }

  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode(!isMultiSelectMode)
    setSelectedIcons([])
  }

  const toggleIconSelection = (iconId: number) => {
    if (selectedIcons.includes(iconId)) {
      setSelectedIcons(selectedIcons.filter((id) => id !== iconId))
    } else {
      setSelectedIcons([...selectedIcons, iconId])
    }
  }

  const selectAllIcons = () => {
    const allVisibleIds = iconsFiltered.map((icon) => icon.id)
    setSelectedIcons(allVisibleIds)
  }

  const deselectAllIcons = () => {
    setSelectedIcons([])
  }

  const handleBulkDelete = async () => {
    if (selectedIcons.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção!',
        text: 'Selecione pelo menos um ícone para excluir.',
      })
      return
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Confirmar Exclusão',
      text: `Deseja excluir ${selectedIcons.length} ícone(s) selecionado(s)?`,
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
    })

    if (result.isConfirmed) {
      try {
        const deletePromises = selectedIcons.map((iconId) => DELETE(`/svgwebapp/${iconId}`))

        const responses = await Promise.all(deletePromises)
        const successfulDeletes = responses.filter(
          (response) => response && response.statusCode === 200 && response.data?.success
        )

        if (successfulDeletes.length === selectedIcons.length) {
          const updatedIcons = icons.filter((icon) => !selectedIcons.includes(icon.id))
          setIcons(updatedIcons)

          if (selectedIcons.includes(selectedIconId)) {
            setSelectedIconId(0)
            setSelectedIconSvg('')
          }

          changeFilterIcons()
          setSelectedIcons([])
          setIsMultiSelectMode(false)

          Swal.fire({
            icon: 'success',
            title: 'Sucesso!',
            text: `${successfulDeletes.length} ícone(s) excluído(s) com sucesso.`,
            timer: 2000,
            showConfirmButton: false,
          })
        } else {
          throw new Error(
            `Apenas ${successfulDeletes.length} de ${selectedIcons.length} ícones foram excluídos`
          )
        }
      } catch (error: any) {
        console.error('Erro ao excluir ícones:', error)
        Swal.fire({
          icon: 'error',
          title: 'Erro!',
          text: 'Erro ao excluir alguns ícones. Tente novamente.',
        })
      }
    }
  }

  async function fetchCategories() {
    try {
      setIsLoading(true)
      const res = await GET(`/classification-posts`)
      const newCategories = res.data
        .filter((row: CategoryType) => row.status)
        .map((row: CategoryType) => {
          return {
            id: row.id,
            name: `${row.name}`,
            status: row.status,
            code: row.code,
          }
        })
      setCategories(newCategories)
    } catch (error) {
      console.error('Error fetching categories:', error)
    } finally {
      setIsLoading(false)
    }
  }
  const fetchStoresAndRestaurants = async () => {
    try {
      setIsLoading(true)
      const response = await POST('/stores-hotel/filter', {
        vinculationType: 'dynamic',
      })

      if (response) {
        const mapped: Store[] = response.data.map((store: Store) => ({
          id: store.id,
          pluginspaceId: store.pluginspaceId,
          link: store.link,
          description: store.description,
          code: store.code,
          status: store.status,
          paymentIntegrated:
            store.paymentIntegrated === true
              ? i18n.t('stores_pages.table.status_active')
              : i18n.t('stores_pages.table.status_inactive'),
          created: store.created,
          updated: store.updated,
          order: store.order,
          img: store.img,

          // mapeando de dentro do pluginspace
          name: store.pluginspace?.name ?? '',
          cnpj: store.pluginspace?.cnpj ?? '',
          type: store.pluginspace?.type ?? '',
        }))

        setStores(mapped)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  async function fetchIcons() {
    try {
      const res = await GET('svgwebapp')
      if (res && res.success) {
        setIcons(res.data)
        setIconsFiltered(res.data)
      }

      // Fetch icon categories
      const svgCategory = await POST('svgcategory/filter', {
        status: true,
      })

      const auxCategory = [
        {
          select: 'Todos',
          value: -1,
        },
      ]

      svgCategory.data.map((el: any) => {
        auxCategory.push({
          select: el.name,
          value: el.id,
        })
        return el
      })

      setIconCategories(auxCategory)
    } catch (error) {
      console.error('Error fetching icons:', error)
    }
  }

  function filterIcons() {
    try {
      let filtered = icons

      // Filter by category
      if (selectedIconCategory !== -1 && selectedIconCategory !== `-1`) {
        filtered = filtered.filter((icon) => icon?.category?.toString() === selectedIconCategory)
      }

      // Filter by search query
      if (iconSearchQuery.trim()) {
        filtered = filtered.filter((icon) =>
          icon?.description?.toLowerCase().includes(iconSearchQuery.toLowerCase())
        )
      }

      setIconsFiltered(filtered)
    } catch (error) {
      console.error('Error filtering icons:', error)
    }
  }

  function selectIcon(icon: IconType) {
    setSelectedIconId(icon.id)
    setSelectedIconSvg(icon.svg)
  }

  function validateForm(): boolean {
    if (!title.trim()) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_section.messages.validation.title_required')),
        text: String(i18n.t('hospitality_section.messages.validation.title_required')),
      })
      return false
    }

    // ✅ Validação diferente para tipo PAGE (múltiplas lojas)
    if (type.value === sectionType.page) {
      if (selectedStores.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Selecione pelo menos uma loja',
          text: 'Você precisa selecionar ao menos uma loja para criar a página',
        })
        return false
      }
    } else {
      // Validação para outros tipos (single item)
      if (selectedItemId.value === 0) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('hospitality_section.messages.validation.category_required')),
          text: String(i18n.t('hospitality_section.messages.validation.category_required')),
        })
        return false
      }
    }

    if (selectedIconId === 0) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_section.messages.validation.icon_required')),
        text: String(i18n.t('hospitality_section.messages.validation.icon_required')),
      })
      return false
    }

    return true
  }

  async function createSection() {
    try {
      if (!validateForm()) {
        return
      }

      setIsLoading(true)

      let sectionData: SectionData

      if (type.value === sectionType.page) {
        // ✅ NOVO FORMATO - Para múltiplas lojas (type = page)
        sectionData = {
          title: title.trim(),
          svgId: selectedIconId.toString(),
          type: type.value,
          status: status,
          items: selectedStores.map((store, index) => ({
            itemId: store.id,
            itemType: sectionType.page, // tipo fixo para lojas
            order: index + 1,
          })),
        }
      } else {
        // ✅ NOVO FORMATO - Para single item (category, form, etc)
        sectionData = {
          title: title.trim(),
          svgId: selectedIconId.toString(),
          type: type.value,
          status: status,
          items: [
            {
              itemId: selectedItemId.value,
              itemType: type.value as any, // converte o sectionType para itemType
              order: 1,
            },
          ],
        }
      }

      // Implementar quando tiver endpoint
      const res = await POST('/hotel-post-pages', sectionData)

      Swal.fire({
        icon: 'success',
        title: String(i18n.t('hospitality_section.messages.success.created')),
        text: String(i18n.t('hospitality_section.messages.success.created')),
      }).then(() => {
        // Reset form
        setTitle('')
        setType({
          value: sectionType.simple,
          select: String(i18n.t('hospitality_section.types.simple')),
        })
        setStatus(true)
        setSelectedItemId({selected: '', value: 0})
        setSelectedIconId(0)
        setSelectedIconSvg('')

        // Navigate back
        navigate('/hospitality/content/section')
      })
    } catch (error) {
      console.error('Error creating section:', error)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_section.messages.error.create')),
        text: String(i18n.t('hospitality_section.messages.error.create')),
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadContentbyType()
    fetchIcons()
  }, [])

  async function loadContentbyType() {
    switch (type.value) {
      case sectionType.category:
        await fetchCategories()
        break
      case sectionType.page:
        // Load pages
        await fetchStoresAndRestaurants()
        break
      case sectionType.content:
      // Load contents
      case sectionType.simple:
      // Load simples
      case sectionType.form:
      // Load forms
      default:
        break
    }
    setIsLoading(false)
  }

  useEffect(() => {
    loadContentbyType()
  }, [type])

  useEffect(() => {
    changeFilterIcons()
  }, [changeFilterIcons])

  if (isLoading) {
    return <Loading />
  }

  return (
    <div className='row g-5 gx-xxl-12'>
      <div className='d-flex justify-content-between'>
        <div className='w-50 me-5'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('hospitality_section.edit.page_title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('hospitality_section.edit.page_subtitle')}
                </span>
              </h3>
            </div>
            <div className='card-body'>
              <div className='row'>
                {/* Formulário - 2/3 da tela */}
                <div className='col-md-12'>
                  {/* Card dos Campos do Formulário */}
                  <div className='card mb-5'>
                    <div className='card-header'>
                      <h5 className='card-title m-0'>Informações da Seção</h5>
                    </div>
                    <div className='card-body'>
                      <div className='row'>
                        <div className='col-12'>
                          {/* Título */}
                          <Input
                            inputAttr={{
                              type: 'default',
                              required: true,
                            }}
                            label={i18n.t('hospitality_section.fields.title')}
                            placeholder={i18n.t('hospitality_section.placeholders.title')}
                            value={title}
                            change={(value: string) => setTitle(value)}
                            required={true}
                          />

                          {/* Tipo */}
                          <Input
                            inputAttr={{
                              type: 'select',
                              required: true,
                            }}
                            label={i18n.t('hospitality_section.fields.type')}
                            placeholder={i18n.t('hospitality_section.placeholders.type')}
                            options={typeOptions}
                            search={true}
                            value={type}
                            change={(value: {value: sectionType; select: string}) => {
                              setType(value)
                            }}
                            required={true}
                          />

                          {/* ✅ Categoria - Mostrar apenas se NÃO for tipo PAGE */}
                          {type.value === sectionType.category && (
                            <Input
                              inputAttr={{
                                type: 'select',
                                required: true,
                              }}
                              label={i18n.t('hospitality_section.fields.category')}
                              placeholder={i18n.t('hospitality_section.placeholders.category')}
                              options={categoryOptions}
                              search={true}
                              value={selectedItemId}
                              change={(value: any) => setSelectedItemId(value)}
                              required={true}
                            />
                          )}

                          {/* ✅ Lojas Selecionadas - Mostrar apenas se for tipo PAGE */}
                          {type.value === sectionType.page && (
                            <>
                              <div className='row g-5  mt-5'>
                                {stores.map((store) => {
                                  const isSelected = selectedStores.some((b) => b.id === store.id)
                                  return (
                                    <div key={store.id} className='col-lg-6 col-md-6'>
                                      <div
                                        className={`card cursor-pointer ${
                                          isSelected ? 'border-primary' : ''
                                        }`}
                                        onClick={() => {
                                          if (isSelected) {
                                            setSelectedStores(
                                              selectedStores.filter((b) => b.id !== store.id)
                                            )
                                          } else {
                                            setSelectedStores([...selectedStores, store])
                                          }
                                        }}
                                      >
                                        <CardStoreHotel store={store} isSelected={isSelected} />
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </>
                          )}

                          <Input
                            inputAttr={{
                              type: 'checkbox',
                            }}
                            label={i18n.t('hospitality_section.fields.status')}
                            checked={status}
                            change={(value: boolean) => setStatus(value)}
                            checkSwift={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card do Ícone Selecionado */}
                  <div className='card mb-5'>
                    <div className='card-header'>
                      <h5 className='card-title m-0'>
                        {i18n.t('hospitality_post_page.create.icon.icon')}
                      </h5>
                    </div>
                    <div className='card-body'>
                      <div className='d-flex align-items-center gap-4'>
                        <div
                          className='border rounded d-flex align-items-center justify-content-center'
                          style={{
                            width: '80px',
                            height: '80px',
                            backgroundColor: selectedIconSvg ? '#F1FAFF' : '#F5F8FA',
                          }}
                        >
                          {selectedIconSvg ? (
                            <SVG src={selectedIconSvg} style={{width: '40px', height: '40px'}} />
                          ) : (
                            <i className='fas fa-image text-muted fs-1'></i>
                          )}
                        </div>
                        <div className='flex-grow-1'>
                          <div className='d-flex flex-column'>
                            <span className='fw-bold fs-6 mb-1'>
                              {selectedIconSvg
                                ? i18n.t('hospitality_post_page.create.icon.selected')
                                : i18n.t('hospitality_post_page.create.icon.no_selected')}
                            </span>
                            <span className='text-muted fs-7'>
                              {selectedIconSvg
                                ? i18n.t('hospitality_post_page.create.icon.selected_description')
                                : i18n.t(
                                    'hospitality_post_page.create.icon.no_selected_description'
                                  )}
                            </span>
                          </div>
                        </div>
                        {selectedIconSvg && (
                          <Button
                            text={i18n.t('hospitality_experiences.btn_remove')}
                            color='danger'
                            size='small'
                            click={() => {
                              setSelectedIconId(0)
                              setSelectedIconSvg('')
                            }}
                            btnClass='fw-bolder'
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Botões */}
                  <div className='d-flex justify-content-between gap-3 mt-5'>
                    <Button
                      text={i18n.t(`buttons.back`)}
                      color='info'
                      size='small'
                      horizontalPadding={6}
                      btnAttr={{type: 'button'}}
                      click={() => navigate('/hospitality/content/section')}
                      btnClass='fw-bolder'
                    />
                    <Button
                      text={i18n.t(`buttons.save`)}
                      color='primary'
                      size='small'
                      horizontalPadding={6}
                      btnAttr={{type: 'button'}}
                      click={createSection}
                      btnClass='fw-bolder'
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Card de Seleção de Ícones - 1/3 da tela */}
        <div className='w-50 card h-100'>
          <div className='card h-100'>
            <div className='card-header'>
              <h5 className='card-title m-0'>{i18n.t('service_create.icons.title')}</h5>
            </div>
            <div className='card-body p-4'>
              {/* Filtro de categoria e botão de upload */}
              <div className='d-flex justify-content-between align-items-end'>
                <div style={{flex: 1, marginRight: '1rem'}}>
                  <Input
                    inputAttr={{
                      type: 'select',
                    }}
                    label={i18n.t('hospitality_section.fields.category')}
                    options={iconCategories}
                    defaultValue={iconCategories[0]}
                    value={selectedIconCategory || ''}
                    change={(value: any) => {
                      setSelectedIconCategory(value)
                    }}
                  />
                </div>
                {isUserAdmin() && (
                  <div>
                    <Button
                      text='+ Upload Ícone'
                      color='success'
                      size='small'
                      horizontalPadding={3}
                      btnAttr={{
                        type: 'button',
                      }}
                      click={() => setShowUploadModal(true)}
                      btnClass='fw-bolder'
                    />
                  </div>
                )}
              </div>

              {/* Campo de busca de ícones */}
              <div className='mb-4'>
                <Input
                  inputAttr={{
                    type: 'text',
                    placeholder: 'Buscar ícones por descrição...',
                  }}
                  label='Buscar Ícones'
                  value={iconSearchQuery}
                  change={(value: string) => setIconSearchQuery(value)}
                />
              </div>

              {/* Controles de seleção múltipla */}
              {isUserAdmin() && (
                <div className='d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded'>
                  <div className='d-flex align-items-center gap-3'>
                    <Button
                      text={isMultiSelectMode ? 'Cancelar Seleção' : 'Selecionar Múltiplos'}
                      color={isMultiSelectMode ? 'secondary' : 'info'}
                      size='small'
                      click={toggleMultiSelectMode}
                      btnClass='fw-bolder'
                    />

                    {isMultiSelectMode && (
                      <>
                        <Button
                          text='Selecionar Todos'
                          color='light'
                          size='small'
                          click={selectAllIcons}
                          btnClass='fw-bolder'
                        />
                        <Button
                          text='Desmarcar Todos'
                          color='light'
                          size='small'
                          click={deselectAllIcons}
                          btnClass='fw-bolder'
                        />
                      </>
                    )}
                  </div>

                  {isMultiSelectMode && selectedIcons.length > 0 && (
                    <div className='d-flex align-items-center gap-3'>
                      <span className='text-muted'>
                        {selectedIcons.length} ícone(s) selecionado(s)
                      </span>
                      <Button
                        text='Excluir Selecionados'
                        color='danger'
                        size='small'
                        click={handleBulkDelete}
                        btnClass='fw-bolder'
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Grid de Ícones */}
              <div className='d-flex overflow-auto flex-wrap gap-5 justify-content-center'>
                {loadingIcons ? (
                  <div className='w-100 h-100 d-flex justify-content-center align-items-center'>
                    <Loading />
                  </div>
                ) : (
                  <>
                    {iconsFiltered.map((iconF) => (
                      <div
                        key={iconF.id}
                        className={`position-relative d-flex flex-column p-4 justify-content-center align-items-center ${
                          isMultiSelectMode && selectedIcons.includes(iconF.id)
                            ? 'border border-warning border-2 bg-warning bg-opacity-10'
                            : iconF.id === selectedIconId
                            ? 'border border-success border-2'
                            : 'border border-back border-2'
                        }`}
                        style={{
                          width: '20%',
                          cursor: 'pointer',
                        }}
                      >
                        {/* Checkbox para seleção múltipla */}
                        {isMultiSelectMode && (
                          <input
                            type='checkbox'
                            className='form-check-input position-absolute'
                            style={{
                              top: '5px',
                              left: '5px',
                              width: '20px',
                              height: '20px',
                              zIndex: 10,
                            }}
                            checked={selectedIcons.includes(iconF.id)}
                            onChange={() => toggleIconSelection(iconF.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}

                        {/* Botão de Editar no canto superior direito */}
                        {!isMultiSelectMode && isUserAdmin() && (
                          <button
                            className='btn btn-sm btn-primary position-absolute'
                            style={{
                              top: '5px',
                              right: '5px',
                              width: '25px',
                              height: '25px',
                              padding: '0',
                              borderRadius: '50%',
                              fontSize: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 10,
                            }}
                            onClick={(e) => {
                              e.stopPropagation()
                              openEditModal(iconF)
                            }}
                            title='Editar ícone'
                          >
                            ✏️
                          </button>
                        )}

                        {/* Área clicável para seleção do ícone */}
                        <div
                          className='w-100 h-100 d-flex flex-column justify-content-center align-items-center'
                          onClick={() => {
                            if (isMultiSelectMode) {
                              toggleIconSelection(iconF.id)
                            } else {
                              selectIcon(iconF)
                            }
                          }}
                        >
                          <span
                            className='mb-2 text-center'
                            style={{fontSize: '0.8rem', wordBreak: 'break-word'}}
                          >
                            {iconF.description}
                          </span>

                          {/* Renderização condicional do ícone */}
                          {iconF.svg &&
                            (iconF.svg.includes('<svg') ? (
                              // SVG inline
                              <div
                                dangerouslySetInnerHTML={{__html: iconF.svg}}
                                style={{
                                  width: '45px',
                                  height: '45px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              />
                            ) : iconF.svg.startsWith('data:image/svg+xml') ||
                              iconF.svg.endsWith('.svg') ? (
                              // SVG como URL ou data URL
                              <SVG
                                src={iconF.svg}
                                style={{
                                  width: '45px',
                                  height: '45px',
                                }}
                                onError={() => {
                                  console.warn(`Erro ao carregar SVG: ${iconF.description}`)
                                }}
                              />
                            ) : (
                              // Fallback para ícones inválidos
                              <div
                                style={{
                                  width: '45px',
                                  height: '45px',
                                  border: '2px dashed #ccc',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.7rem',
                                  color: '#999',
                                  textAlign: 'center',
                                }}
                              >
                                SVG inválido
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Upload de SVG */}
      <SVGUploadModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        onIconUploaded={handleIconUploaded}
        categories={iconCategories}
      />

      {/* Modal de Edição de Ícone */}
      <IconEditModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        icon={editingIcon}
        onSave={handleIconUpdated}
      />
    </div>
  )
}

const SectionCreate: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: true},
          {
            title: i18n.t('hospitality_section.create.page_title'),
            path: 'hospitality/feed/content',
            isActive: true,
          },
        ]}
      >
        {i18n.t('hospitality_section.create.page_title')}
      </PageTitle>
      <Content />
    </>
  )
}

export {SectionCreate}
